import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaHandshake, 
  FaUsers, 
  FaCalculator,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaClock,
  FaUserCheck,
  FaListAlt,
  FaCalendarCheck,
  FaFileInvoiceDollar,
  FaChartLine,
  FaShoppingCart,
  FaBullhorn,
  FaTasks,
  FaChartBar,
  FaFileInvoice,
  FaReceipt,
  FaBalanceScale,
  FaCoins,
  FaBoxes,
  FaLaptopCode
} from 'react-icons/fa';
import './ModuleContent.css';

import musteriImg from '../../assets/modüller/musteri.png';

const ModuleContent = ({ activeModule }) => {
  const [activeFeature, setActiveFeature] = useState(null);

  const imagesByTitle = useMemo(
    () => ({
      // Satış Modülü
      Müşteriler: musteriImg
    }),
    []
  );

  useEffect(() => {
    if (!activeFeature) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setActiveFeature(null);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [activeFeature]);

  const modules = {
    satis: {
      title: 'Satış Modülü',
      icon: <FaHandshake />,
      description: 'Satış süreçlerinizi profesyonelce yönetin, müşteri ilişkilerinizi güçlendirin ve satış performansınızı artırın.',
      features: [
        {
          icon: <FaUsers />,
          title: 'Müşteriler',
          description: 'Müşteri bilgilerinizi yönetin, müşteri ürün envanterini takip edin ve anlaşmalı müşterilerinizi organize edin.',
          subFeatures: [
            'Müşteri Ürün Envanteri',
            'Bakım / Destek Anlaşmalı Müşteriler',
            'Alan Adı ve Barındırma Hizmeti Müşterileri'
          ]
        },
        {
          icon: <FaBullhorn />,
          title: 'Satış Görüşmeleri',
          description: 'Satış görüşmelerinizi organize edin, satış temasları oluşturun ve satış süreçlerinizi takip edin.',
          subFeatures: [
            'Satış Teması Oluştur',
            'Satış Temasları'
          ]
        },
        {
          icon: <FaShoppingCart />,
          title: 'Yenileme Satışlar',
          description: 'Yenileme satışlarınızı yönetin, müşteri yenileme süreçlerini takip edin ve otomatik hatırlatmalar ile hiçbir yenilemeyi kaçırmayın.'
        },
        {
          icon: <FaChartLine />,
          title: 'Satış Takibi',
          description: 'Satış süreçlerinizi takip edin, satış görüşmelerini kaydedin ve detaylı satış raporları ile performansınızı analiz edin.'
        },
        {
          icon: <FaTasks />,
          title: 'İş Planları',
          description: 'Satış hedeflerinizi belirleyin, iş planlarınızı oluşturun ve takip edin. Önceliklendirme ve durum yönetimi ile verimliliğinizi artırın.'
        },
        {
          icon: <FaChartBar />,
          title: 'Satış Raporları',
          description: 'Gerçek zamanlı dashboard, detaylı satış raporları ve Excel/PDF export özellikleri ile satış performansınızı veri odaklı yönetin.'
        },
        {
          icon: <FaFileInvoice />,
          title: 'Teklif ve Sözleşme Yönetimi',
          description: 'Müşterilere teklif hazırlayın, sözleşmeleri yönetin ve takip edin. Otomatik hatırlatmalar ile hiçbir sözleşmeyi kaçırmayın.'
        }
      ]
    },
    'insan-kaynaklari': {
      title: 'İnsan Kaynakları Modülü',
      icon: <FaUsers />,
      description: 'Personel yönetimi, izin takibi, avans yönetimi ve PDKS sistemi ile ekibinizi etkili bir şekilde yönetin.',
      features: [
        {
          icon: <FaCalendarAlt />,
          title: 'İzin Yönetimi',
          description: 'Personel izin taleplerini yönetin, onaylayın ve takip edin. İzin taleplerim, tüm izin talepleri ve personel izinleri modülleri ile kapsamlı izin yönetimi.',
          subFeatures: [
            'İzin Taleplerim',
            'Tüm İzin Talepleri',
            'Personel İzinleri'
          ]
        },
        {
          icon: <FaMoneyBillWave />,
          title: 'Avans Yönetimi',
          description: 'Personel avans taleplerini yönetin, onaylayın ve takip edin. Avans taleplerim ve tüm avans talepleri modülleri ile kolay avans yönetimi.',
          subFeatures: [
            'Avans Taleplerim',
            'Tüm Avans Talepleri'
          ]
        },
        {
          icon: <FaClock />,
          title: 'PDKS (Personel Devam Kontrol Sistemi)',
          description: 'Personel devam kontrolü, vardiya yönetimi ve puantaj takibi ile çalışma saatlerini profesyonelce yönetin.',
          subFeatures: [
            'Kullanıcılar',
            'Vardiya Modelleri',
            'Puantaj'
          ]
        },
        {
          icon: <FaUserCheck />,
          title: 'Personel Yönetimi',
          description: 'Personel bilgilerini yönetin, performans takibi yapın ve ekibinizi etkili bir şekilde organize edin.'
        }
      ]
    },
    muhasebe: {
      title: 'Muhasebe Modülü',
      icon: <FaCalculator />,
      description: 'Mali işlemlerinizi takip edin, bütçe yönetimi yapın ve finansal raporlar ile işletmenizin mali durumunu kontrol altında tutun.',
      features: [
        {
          icon: <FaFileInvoiceDollar />,
          title: 'Fatura Yönetimi',
          description: 'Gelen ve giden faturaları yönetin, fatura takibi yapın ve mali işlemlerinizi organize edin.'
        },
        {
          icon: <FaReceipt />,
          title: 'Gider Yönetimi',
          description: 'İşletme giderlerinizi kaydedin, kategorize edin ve takip edin. Detaylı gider raporları ile maliyetlerinizi kontrol altında tutun.'
        },
        {
          icon: <FaBalanceScale />,
          title: 'Bütçe Takibi',
          description: 'Bütçe planlaması yapın, bütçe takibi gerçekleştirin ve bütçe sapmalarını analiz edin. Gerçek zamanlı bütçe raporları ile mali durumunuzu izleyin.'
        },
        {
          icon: <FaChartBar />,
          title: 'Finansal Raporlar',
          description: 'Gelir-gider raporları, kar-zarar analizleri ve finansal dashboard ile işletmenizin mali durumunu detaylı bir şekilde görüntüleyin.'
        },
        {
          icon: <FaCoins />,
          title: 'Tahsilat Yönetimi',
          description: 'Müşteri tahsilatlarını takip edin, ödeme planları oluşturun ve tahsilat durumlarını yönetin.'
        },
        {
          icon: <FaFileInvoice />,
          title: 'Muhasebe Entegrasyonu',
          description: 'Muhasebe sistemleri ile entegrasyon, otomatik veri aktarımı ve senkronizasyon özellikleri ile verimli muhasebe yönetimi.'
        }
      ]
    },
    envanter: {
      title: 'Envanter Modülü',
      icon: <FaBoxes />,
      description: 'Ürün, stok ve şirket varlıklarınızı tek ekrandan yönetin. Giriş/çıkış hareketleri, depo-lokasyon takibi ve raporlarla envanterinizi kontrol altında tutun.',
      features: [
        {
          icon: <FaListAlt />,
          title: 'Ürün & Stok Kartları',
          description: 'Ürün kartlarını, birimlerini ve kritik stok seviyelerini yönetin; stok durumunu anlık olarak görüntüleyin.',
          subFeatures: [
            'Ürün Kartları ve Kategoriler',
            'Kritik Stok Seviyeleri',
            'Stok Durumu (Anlık)'
          ]
        },
        {
          icon: <FaShoppingCart />,
          title: 'Giriş / Çıkış Hareketleri',
          description: 'Stok giriş-çıkışlarını kayıt altına alın, iade ve sarf hareketlerini takip edin; geçmişe dönük izlenebilirlik sağlayın.',
          subFeatures: [
            'Stok Girişi / Çıkışı',
            'İade ve Sarf Takibi',
            'Hareket Geçmişi'
          ]
        },
        {
          icon: <FaCalendarCheck />,
          title: 'Sayım & Mutabakat',
          description: 'Periyodik envanter sayımları yapın, sayım farklarını raporlayın ve kayıtlarınızı doğrulayın.',
          subFeatures: [
            'Envanter Sayımı',
            'Sayım Fark Raporu',
            'Mutabakat / Düzeltme'
          ]
        },
        {
          icon: <FaChartBar />,
          title: 'Raporlama',
          description: 'Stok değer raporları, hareket raporları ve analiz ekranlarıyla envanter performansınızı ölçün.',
          subFeatures: [
            'Stok Değer Raporu',
            'Hareket Raporları',
            'Excel/PDF Dışa Aktarım'
          ]
        }
      ]
    },
    'bilgi-teknolojileri': {
      title: 'Bilgi Teknolojileri Modülü',
      icon: <FaLaptopCode />,
      description: 'BT operasyonlarınızı uçtan uca yönetin. Destek talepleri, varlık yönetimi, bakım planları ve raporlamayla ekip verimliliğini artırın.',
      features: [
        {
          icon: <FaTasks />,
          title: 'Destek Talepleri (Ticket)',
          description: 'Destek taleplerini kaydedin, atayın ve durum/öncelik yönetimi ile SLA’ları takip edin.',
          subFeatures: [
            'Talep Oluşturma ve Kategorileme',
            'Atama ve Durum Yönetimi',
            'Öncelik & SLA Takibi'
          ]
        },
        {
          icon: <FaBoxes />,
          title: 'Varlık & Lisans Yönetimi',
          description: 'Cihaz, ekipman ve yazılım lisanslarını envanterleyin; garanti/bakım bilgilerini tek yerde tutun.',
          subFeatures: [
            'Donanım / Ekipman Envanteri',
            'Yazılım Lisans Takibi',
            'Garanti / Bakım Bilgileri'
          ]
        },
        {
          icon: <FaUserCheck />,
          title: 'Erişim & Yetki Süreçleri',
          description: 'Erişim taleplerini kayıt altına alın, onay süreçlerini yönetin ve kim hangi erişime sahip takip edin.',
          subFeatures: [
            'Erişim Talepleri',
            'Onay Akışları',
            'Yetki / Erişim Envanteri'
          ]
        },
        {
          icon: <FaCalendarAlt />,
          title: 'Bakım & Planlama',
          description: 'Planlı bakım ve iyileştirme çalışmalarını takvimleyin; kesinti ve bakım süreçlerini görünür kılın.',
          subFeatures: [
            'Bakım Takvimi',
            'Planlı Çalışmalar',
            'Hatırlatmalar'
          ]
        },
        {
          icon: <FaChartBar />,
          title: 'Raporlama',
          description: 'Talep yoğunluğu, çözüm süreleri ve ekip performansı gibi metrikleri raporlayın; iyileştirme alanlarını netleştirin.',
          subFeatures: [
            'Talep Analizi',
            'Çözüm Süreleri',
            'Ekip Performansı'
          ]
        }
      ]
    }
  };

  const currentModule = modules[activeModule] || modules.satis;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div
      className="module-content"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      {/* Module Header */}
      <motion.div
        className="module-header"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="module-title">{currentModule.title}</h2>
        <p className="module-description">{currentModule.description}</p>
      </motion.div>

      {/* Features Grid */}
      <motion.div
        className="module-features-grid"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {currentModule.features.map((feature, index) => (
          <motion.div
            key={index}
            className="module-feature-card"
            variants={itemVariants}
            whileHover={{ y: -5 }}
            role={imagesByTitle[feature.title] ? 'button' : undefined}
            tabIndex={imagesByTitle[feature.title] ? 0 : undefined}
            onClick={() => {
              if (imagesByTitle[feature.title]) setActiveFeature(feature);
            }}
            onKeyDown={(e) => {
              if (!imagesByTitle[feature.title]) return;
              if (e.key === 'Enter' || e.key === ' ') setActiveFeature(feature);
            }}
          >
            <div className="module-feature-icon" style={{ color: 'var(--primary-color)' }}>
              {feature.icon}
            </div>
            <h3 className="module-feature-title">{feature.title}</h3>
            <p className="module-feature-description">{feature.description}</p>
            {feature.subFeatures && (
              <ul className="module-sub-features">
                {feature.subFeatures.map((subFeature, subIndex) => (
                  <li key={subIndex}>{subFeature}</li>
                ))}
              </ul>
            )}
          </motion.div>
        ))}
      </motion.div>

      {activeFeature && imagesByTitle[activeFeature.title] && (
        <div
          className="module-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`${activeFeature.title} görseli`}
          onMouseDown={() => setActiveFeature(null)}
        >
          <div
            className="module-modal"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="module-modal-close"
              aria-label="Kapat"
              onClick={() => setActiveFeature(null)}
            >
              ×
            </button>
            <img
              className="module-modal-img"
              src={imagesByTitle[activeFeature.title]}
              alt={activeFeature.title}
              loading="eager"
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ModuleContent;

